<script setup lang="ts">
    import { ref, computed } from 'vue'
    import { boolCompare, isNullOrWhitespace, numCompare } from '@common/Common'

    import type { ScoringVoteEntry } from '@Evaluation/AlternativesSurveys/Domain/readmodels'
    import type { DiscreteScaleLevelReadModel } from '@Scales/readmodels'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        propIsMeetingManager: boolean
        propContextName: string
        propAlternativeName: string
        propVotesEntries: ScoringVoteEntry[]
        propScaleLevels: DiscreteScaleLevelReadModel[]
        propValue?: number | null
        propComment?: string | null
    }>()

    const emit = defineEmits<{
        (e: 'update-vote-value', value: number | null): void
        (e: 'update-vote-comment', comment: string): void
        (e: 'show-alternative-details'): void
        (e: 'show-criterion-details'): void
    }>()

    /* ---------------- local state ---------------- */

    const ActiveTab = ref<'votes' | 'summary' | 'notes'>('votes')

    /* ---------------- derived ---------------- */

    const isMeetingManager = computed(() => props.propIsMeetingManager)
    const contextName = computed(() => props.propContextName)
    const alternativeName = computed(() => props.propAlternativeName)

    const votesEntries = computed(() => props.propVotesEntries)
    const scaleLevels = computed(() => props.propScaleLevels)

    const value = computed<number | null>(() => props.propValue ?? null)
    const comment = computed<string | null>(() => props.propComment ?? null)

    /* ---------------- scale helpers ---------------- */

    const QuestionScale = computed<number[]>(() =>
        scaleLevels.value
            .map(l => l.Value)
            .sort((a, b) => numCompare(a, b))
    )

    /* ---------------- table helpers ---------------- */

    function getDrTableCells(val: number | null): TdViewModel[]
    {
        const cells = QuestionScale.value.map(v => new TdViewModel(v, false, false))
        if (val == null) return cells

        const exact = cells.find(c => Math.round(c.Value * 100) === Math.round(val * 100))
        if (exact)
        {
            exact.Highlighted = true
            return cells
        }

        const prevCount = QuestionScale.value.filter(v => v < val).length
        const prev = cells[prevCount - 1]

        prev.Highlighted = true
        prev.Value = val
        prev.Colspan = 2

        cells.splice(prevCount, 1)
        return cells
    }

    /* ---------------- votes view ---------------- */

    const VotesEntriesView = computed(() =>
        votesEntries.value
            .map(e => new VoteEntryViewModel(getDrTableCells, e))
            .sort((a, b) => boolCompare(a.SurveyId != null, b.SurveyId != null))
    )

    const Comments = computed(() =>
        votesEntries.value.filter(e => !isNullOrWhitespace(e.Comment))
    )

    /* ---------------- averages & histogram ---------------- */

    const Average = computed<number | null>(() =>
    {
        const valid = votesEntries.value.filter(e => e.Value != null && e.VoteStrength > 0)
        if (!valid.length) return null

        const weightedSum = valid.reduce(
            (sum, e) => sum + e.VoteStrength * e.Value,
            0
        )
        const weights = valid.reduce((sum, e) => sum + e.VoteStrength, 0)

        return weightedSum / weights
    })

    const AverageCells = computed(() => getDrTableCells(Average.value))

    const HistogramValues = computed(() =>
    {
        const voteValues = votesEntries.value.filter(e => e.Value != null).map(e => e.Value)
        const uniqueValues = new Set([...QuestionScale.value, ...voteValues])
        const orderedValues = [...uniqueValues].sort((a, b) => b - a)

        let maxCount = 0
        for (const v of orderedValues)
        {
            maxCount = Math.max(maxCount, voteValues.filter(x => x === v).length)
        }

        return orderedValues.map(v =>
        {
            const count = voteValues.filter(x => x === v).length
            return {
                Value: v,
                Count: count,
                PercentOfMax: maxCount ? Math.round((count / maxCount) * 100) : 0
            }
        })
    })

    /* ---------------- tab helpers ---------------- */

    const VotesTabActive = computed(() => ActiveTab.value === 'votes')
    const SummaryTabActive = computed(() => ActiveTab.value === 'summary')
    const CommentsTabActive = computed(() => ActiveTab.value === 'notes')

    /* ---------------- actions ---------------- */

    function updateValue(v: number | null)
    {
        emit('update-vote-value', v)
    }

    function updateComment(c: string)
    {
        emit('update-vote-comment', c)
    }

    function showAlternativeDetails()
    {
        emit('show-alternative-details')
    }

    function showCriterionDetails()
    {
        emit('show-criterion-details')
    }

    /* ---------------- local view models ---------------- */

    class VoteEntryViewModel
    {
        Id: string
        VotesSourceName: string
        VoteStrength: number
        Value: number
        Comment: string
        SurveyId?: number
        ProjectUserId?: number

        constructor(
            private readonly getCells: (v: number | null) => TdViewModel[],
            voteEntry: ScoringVoteEntry
        )
        {
            this.Id = `${voteEntry.SurveyId}.${voteEntry.ProjectUserId}`
            this.VotesSourceName = voteEntry.VotesSourceName
            this.VoteStrength = voteEntry.VoteStrength
            this.Value = voteEntry.Value
            this.Comment = voteEntry.Comment
            this.SurveyId = voteEntry.SurveyId
            this.ProjectUserId = voteEntry.ProjectUserId
        }

        get cells(): TdViewModel[]
        {
            return this.getCells(this.Value)
        }
    }

    class TdViewModel
    {
        Colspan: number
        Highlighted: boolean
        Value: number

        constructor(value: number, highlighted: boolean, colspan: boolean)
        {
            this.Value = value
            this.Highlighted = highlighted
            this.Colspan = colspan ? 2 : 1
        }
    }
</script>


<template>
    <div>

        <p class="text-center lead" style="font-size: 1.5rem;">
            How would you rate: <a class="text-primary" title="Show description" @click="showAlternativeDetails">{{alternativeName}}</a><br />
            in the context of: <a class="text-primary" title="Show description" @click="showCriterionDetails">{{contextName}}</a>?
        </p>

        <template v-if="isMeetingManager === true">
            <div class="card card-collapsable mt-1">
                <div class="card-header pointer" data-toggle="collapse" href="#cardQuestion">
                    Group vote
                </div>
                <div id="cardQuestion" class="card-body collapse show pt-4">
                    <div style="min-height: 450px;">
                        <div class="list-group">
                            <a v-for="l in scaleLevels" :key="l.Value" class="list-group-item list-group-item-action text-center"
                               @click="updateValue(l.Value)" :class="{ 'bg-success': value === l.Value, 'bg-white': value !== l.Value }">
                                <strong>{{l.Value}} - {{l.Name}}</strong>
                                <p v-if="l.Definition" class="mb-0">{{l.Definition}}</p>
                            </a>
                        </div>

                        <div class="text-center">
                            <button type="button" class="btn mt-3" @click="updateValue(null)" :class="value == null ? 'btn-success' : 'btn-outline-secondary'">
                                <i class="fa-regular" aria-hidden="true" :class="value == null ? 'fa-square-check' : 'fa-square'"></i>
                                Follow the average (default)
                            </button>
                        </div>

                        <br />

                        <div style="margin-top: 15px;">
                            <textarea rows="5" :value.trim="comment" @input="updateComment($event.target.value)" class="form-control" placeholder="Comment..."></textarea>
                        </div>
                    </div>
                </div>
            </div>

        </template>

        <template v-if="isMeetingManager === false">
            <div style="min-height: 450px;">
                <div class="list-group">
                    <a v-for="l in scaleLevels" :key="l.Value" class="list-group-item list-group-item-action text-center"
                       @click="updateValue(l.Value)" :class="{ 'bg-success': value === l.Value, 'bg-white': value !== l.Value }">
                        <strong>{{l.Value}} - {{l.Name}}</strong>
                        <p v-if="l.Definition" class="mb-0">{{l.Definition}}</p>
                    </a>
                </div>


                <div v-if="value != null" class="text-center">
                    <button type="button" class="btn btn-link mt1" @click="updateValue(null)">
                        <i class="fa fa-times" aria-hidden="true"></i> Clear answer
                    </button>
                </div>

                <br />

                <div style="margin-top: 15px;">
                    <textarea rows="5" :value.trim="comment" @input="updateComment($event.target.value)" class="form-control" placeholder="Comment..."></textarea>
                </div>
            </div>
        </template>

        <div class="mt-4 DirectRateVotesBrowserView" style="min-height: 500px;" v-if="isMeetingManager === true || votesEntries?.length">
            <ul class="nav nav-tabs">
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'votes'" :class="{'active': VotesTabActive}">Votes</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'summary'" :class="{'active': SummaryTabActive}">Chart</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" @click="ActiveTab = 'notes'" :class="{'active': CommentsTabActive}">Comments</a>
                </li>
            </ul>

            <div class="tab-content">
                <div v-if="VotesTabActive" :class="{'active': VotesTabActive}" class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table table-striped">
                            <tbody>
                                <tr style="text-align: center;">
                                    <td style="text-align: left;">
                                        <strong>Average ({{Average != null ? Average.toFixedIfNeeded(2) : 'N/A' }})</strong>
                                    </td>
                                    <td v-for="cell in AverageCells"
                                        :class="{ 'bg-color-gray': cell.Highlighted }" :colspan="cell.Colspan" :style="{'width': cell.Colspan === 1 ? '5%' : '10%'}">
                                        <span :class="{ 'fg-color-white': cell.Highlighted }">
                                            {{cell.Value.toFixedIfNeeded(2)}}
                                        </span>
                                    </td>
                                </tr>

                                <tr style="text-align: center;" v-for="e in VotesEntriesView" :key="e.Id">
                                    <td style="text-align: left;">
                                        {{e.VotesSourceName ?? '???'}}
                                    </td>
                                    <td v-for="cell in e.cells" :key="cell.Value" :colspan="cell.Colspan" :class="{ 'bg-color-green': cell.Highlighted }">
                                        <span :class="{ 'fg-color-white': cell.Highlighted }">
                                            {{cell.Value.toFixedIfNeeded(2)}}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="SummaryTabActive" :class="{'active': SummaryTabActive} " class="tab-pane pt-2">

                    <div class="table-responsive">
                        <table class="table">
                            <tbody>
                                <tr style="text-align: center;" class="bottom noborder">
                                    <td v-for="v in HistogramValues" style="height: 400px; border: none; padding-top: 20px;">
                                        <div v-if="v.Count">{{v.Count}}</div>
                                        <div class="bg-color-green2" :style="{'height': v.PercentOfMax + '%'}"></div>
                                    </td>
                                </tr>
                                <tr style="text-align: center;">
                                    <td v-for="v in HistogramValues" style="width: 5%;">
                                        {{v.Value.toFixedIfNeeded(2)}}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="CommentsTabActive" :class="{ 'active': CommentsTabActive }" class="tab-pane pt-2">
                    <blockquote class="blockquote" v-for="c in Comments">
                        <p class="mb-0">{{c.Comment}}</p>
                        <footer class="blockquote-footer">{{c.VotesSourceName ?? '???'}}</footer>
                    </blockquote>
                </div>
            </div>
        </div>

    </div>
</template>

<style scoped>
    .DirectRate:hover {
        border-color: rgba(86, 86, 90, 0.25);
    }

    .DirectRateVotesBrowserView tr.bottom td {
        vertical-align: bottom !important;
    }
</style>